import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MatchPhone } from "refsix-js-models";

import { RootState } from "../../../redux/store";
import DateFormatFilterComponent from "../../../components/DateFormatFilterComponent";
import {
    formatMetricValue,
    topMatchesByMetric,
    VenueMetric,
} from "../leaderboardModel";

const KEY = "leaderboards.VenuePage.";

/** The design shows the player's five best games at a ground (reference/02). */
const TOP_GAMES = 5;

export interface YourHistoryListProps {
    matches: MatchPhone[];
    metric: VenueMetric;
    /** The match the user tapped through from, highlighted so they can find it in the list. */
    currentMatchId?: string;
    /** Opens a row's match result. Every row here is one of the player's own matches. */
    onOpenMatch: (matchId: string) => void;
}

/**
 * "Your history here" — the player's best games at this ground (reference/02 `.hist-row`).
 *
 * Entirely local: it reads the matches already in redux, so it works at a venue nobody else has
 * ever tracked, which is where most venues will sit for a while yet.
 */
export const YourHistoryList: FC<YourHistoryListProps> = ({
    matches,
    metric,
    currentMatchId,
    onOpenMatch,
}) => {
    const { t } = useTranslation();
    const { useImperial } = useSelector(
        (root: RootState) => root.settings.settings
    );

    const games = topMatchesByMetric(matches, metric, TOP_GAMES);

    if (!games.length) {
        return (
            <div className="venue-history-card">
                <div
                    className="venue-history-empty"
                    data-testid="venue-history-empty"
                >
                    {t(KEY + "noHistoryForMetric")}
                </div>
            </div>
        );
    }

    return (
        <div className="venue-history-card" data-testid="venue-history-list">
            {games.map((game, index) => {
                const value = formatMetricValue(
                    metric,
                    game.value,
                    useImperial,
                    t
                );
                const isCurrentMatch = Boolean(
                    currentMatchId && game.matchId === currentMatchId
                );
                return (
                    <button
                        type="button"
                        key={game.matchId || index}
                        className={`venue-history-row venue-row-tappable${
                            isCurrentMatch ? " venue-history-row-current" : ""
                        }`}
                        data-testid="venue-history-row"
                        disabled={!game.matchId}
                        onClick={() =>
                            game.matchId && onOpenMatch(game.matchId)
                        }
                    >
                        <span className="venue-history-rank">{index + 1}</span>
                        <div className="venue-history-body">
                            <div className="venue-history-teams">
                                {`${game.homeTeam} v ${game.awayTeam}`}
                                {game.score && (
                                    <span className="venue-history-score">
                                        {game.score}
                                    </span>
                                )}
                                {game.isPB && (
                                    <span className="venue-history-pb-tag">
                                        {t(KEY + "pbTag")}
                                    </span>
                                )}
                                {isCurrentMatch && (
                                    <span
                                        className="venue-history-this-tag"
                                        data-testid="venue-history-this-match"
                                    >
                                        {t(KEY + "thisMatch")}
                                    </span>
                                )}
                            </div>
                            <div className="venue-history-date">
                                <DateFormatFilterComponent
                                    value={game.date}
                                    formatString="ll"
                                />
                            </div>
                        </div>
                        <span className="venue-history-value">
                            {value.value}
                            {value.unit && <small> {value.unit}</small>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
